import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, Copy, Check, Pencil, AlertCircle } from "lucide-react";

function imageUrl(path) {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return `${BACKEND_URL}${path}`;
}

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [editing, setEditing] = useState(null); // {id, alt_text, original_filename}
  const [editAlt, setEditAlt] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/media", { params: { limit: 200 } });
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Image uploaded");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/media/${id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const openEdit = (m) => {
    setEditing(m);
    setEditAlt(m.alt_text || "");
  };

  const closeEdit = () => {
    setEditing(null);
    setEditAlt("");
  };

  const saveAlt = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.patch(`/admin/media/${editing.id}`, { alt_text: editAlt });
      toast.success("Alt text saved");
      closeEdit();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const copyUrl = async (m) => {
    const url = imageUrl(m.webp_paths?.lg || m.webp_paths?.md);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(m.id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch { /* noop */ }
  };

  const missingAltCount = items.filter((m) => !m.alt_text || !m.alt_text.trim()).length;

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-media-page">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-primary/80">CMS</p>
            <h1 className="font-display text-4xl sm:text-5xl mt-2">Media library</h1>
          </div>
          {missingAltCount > 0 && (
            <span
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-amber-500/40 text-amber-300 bg-amber-500/5"
              data-testid="media-missing-alt-warning"
            >
              <AlertCircle className="w-3.5 h-3.5" /> {missingAltCount} image{missingAltCount === 1 ? "" : "s"} missing alt text
            </span>
          )}
        </div>

        <label
          className="block border border-dashed border-border rounded-xl p-8 text-center hover:border-primary/40 cursor-pointer bg-card transition focus-within:border-primary"
          data-testid="media-upload-zone"
        >
          <input
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              Array.from(e.target.files || []).forEach((f) => upload(f));
              e.target.value = "";
            }}
            data-testid="media-upload-input"
            aria-label="Upload images"
          />
          <Upload className="w-6 h-6 mx-auto text-primary mb-2" aria-hidden />
          <div className="text-sm">
            {uploading ? "Uploading…" : "Click here or drop images (JPG / PNG / WEBP, max 10 MB)"}
          </div>
        </label>

        {loading ? (
          <div className="py-16 flex justify-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="media-grid">
            {items.length === 0 && <div className="col-span-5 text-center text-muted-foreground py-10">No images yet.</div>}
            {items.map((m) => {
              const url = imageUrl(m.webp_paths?.thumb || m.webp_paths?.md);
              const hasAlt = m.alt_text && m.alt_text.trim().length > 0;
              return (
                <div key={m.id} className="group border border-border rounded-lg overflow-hidden bg-card" data-testid={`media-card-${m.id}`}>
                  <div className="aspect-square bg-secondary relative">
                    {url && <img src={url} alt={m.alt_text || ""} className="w-full h-full object-cover" loading="lazy" width="240" height="240" />}
                    {!hasAlt && (
                      <span
                        className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest bg-amber-500/15 text-amber-200 border border-amber-500/30"
                        data-testid={`media-alt-missing-${m.id}`}
                        title="Missing alt text"
                      >
                        <AlertCircle className="w-2.5 h-2.5" /> alt
                      </span>
                    )}
                  </div>
                  <div className="p-2 space-y-1.5">
                    <div className="flex items-center justify-between gap-1">
                      <span className="text-[11px] text-muted-foreground truncate flex-1" title={m.original_filename}>{m.original_filename}</span>
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 w-7 p-0 border-border"
                          onClick={() => openEdit(m)}
                          data-testid={`media-edit-${m.id}`}
                          aria-label="Edit alt text"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 w-7 p-0 border-border"
                          onClick={() => copyUrl(m)}
                          data-testid={`media-copy-${m.id}`}
                          aria-label="Copy image URL"
                        >
                          {copiedId === m.id ? <Check className="w-3.5 h-3.5 text-primary" /> : <Copy className="w-3.5 h-3.5" />}
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 w-7 p-0 border-border text-destructive"
                              data-testid={`media-delete-${m.id}`}
                              aria-label="Delete image"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete image?</AlertDialogTitle>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => remove(m.id)}
                                className="bg-destructive text-destructive-foreground"
                                data-testid={`media-delete-confirm-${m.id}`}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                    <p className="text-[10px] text-muted-foreground line-clamp-2 min-h-[1.5em]" data-testid={`media-alt-text-${m.id}`}>
                      {hasAlt ? m.alt_text : "— no alt text"}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <Dialog open={!!editing} onOpenChange={(o) => !o && closeEdit()}>
          <DialogContent data-testid="media-alt-dialog">
            <DialogHeader>
              <DialogTitle>Edit alt text</DialogTitle>
              <DialogDescription>
                Describe the image for screen readers and SEO. Keep it under 125 characters.
              </DialogDescription>
            </DialogHeader>
            {editing && (
              <div className="space-y-3">
                <div className="aspect-video bg-secondary rounded border border-border overflow-hidden">
                  {(() => {
                    const url = imageUrl(editing.webp_paths?.md || editing.webp_paths?.thumb);
                    return url ? (
                      <img src={url} alt={editAlt || editing.original_filename} className="w-full h-full object-contain" />
                    ) : null;
                  })()}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="media-alt-input">Alt text</Label>
                  <Input
                    id="media-alt-input"
                    value={editAlt}
                    onChange={(e) => setEditAlt(e.target.value)}
                    maxLength={200}
                    placeholder="e.g. Espresso being poured into a black ceramic cup"
                    data-testid="media-alt-input"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    {editAlt.length} / 200 characters
                  </p>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={closeEdit} className="border-border" data-testid="media-alt-cancel">
                Cancel
              </Button>
              <Button
                onClick={saveAlt}
                disabled={saving}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="media-alt-save"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
